import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Store, PackageOpen, MapPin } from "lucide-react";
import { api, eur, ORDER_STATUS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RESTAURANT } from "@/lib/data";

const PENDING = ["nouvelle", "preparee", "prete"];

export default function PickupPanel({ open, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (!user) {
      setOrders([]);
      return;
    }
    setOrders(null);
    api
      .get("/orders/mine")
      .then((res) => setOrders(res.data.filter((o) => PENDING.includes(o.status))))
      .catch(() => setOrders([]));
  }, [open, user]);

  const goCatalogue = () => {
    onClose();
    navigate("/vins");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="pickup-panel">
          <div className="absolute inset-0 bg-[#1C1917]/55 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            className="absolute bottom-0 inset-x-0 bg-[#FAF6F0] rounded-t-3xl max-h-[80vh] flex flex-col app-frame max-w-[520px] mx-auto"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between px-[5cqw] py-[4cqw] border-b border-[#6B1226]/10">
              <div>
                <p className="eyebrow">Retrait au restaurant</p>
                <h2 className="font-display text-[6cqw] font-semibold text-[#1C1917]">Mes enlèvements</h2>
              </div>
              <button onClick={onClose} className="p-[2cqw] rounded-full hover:bg-[#6B1226]/10 text-[#1C1917] transition-colors" data-testid="pickup-panel-close" aria-label="Fermer">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-[5cqw] py-[4cqw] no-scrollbar" data-lenis-prevent>
              {!orders || orders.length === 0 ? (
                <div className="text-center py-[10cqw]" data-testid="pickup-empty">
                  <PackageOpen size={44} className="text-[#C5A059] mx-auto" />
                  <p className="font-display text-[5.4cqw] font-semibold text-[#1C1917] mt-[3cqw]">Aucune commande en attente</p>
                  <p className="text-[3.6cqw] text-[#57534E] mt-[1.5cqw]">Veuillez démarrer votre commande.</p>
                  <button
                    onClick={goCatalogue}
                    className="mt-[5cqw] rounded-full bg-[#6B1226] text-[#FAF6F0] px-[7cqw] py-[3.4cqw] text-[3.4cqw] font-bold hover:bg-[#580E1D] transition-colors"
                    data-testid="pickup-start-order"
                  >
                    Démarrer ma commande
                  </button>
                </div>
              ) : (
                <ul className="space-y-[3cqw]" data-testid="pickup-orders-list">
                  {orders.map((order) => {
                    const status = ORDER_STATUS[order.status] || ORDER_STATUS.nouvelle;
                    return (
                      <li key={order.id} className="bg-white rounded-2xl border border-[#6B1226]/10 p-[4cqw]" data-testid="pickup-order-card">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-eyebrow text-[2.6cqw] tracking-[0.25em] text-[#6B1226]">N° {order.number}</p>
                          <span className={`rounded-full px-[2.6cqw] py-[1cqw] text-[2.2cqw] font-semibold ${status.classes}`}>{status.label}</span>
                        </div>
                        <p className="text-[3.2cqw] text-[#57534E] mt-[2cqw]">
                          {order.total_qty} bouteille{order.total_qty > 1 ? "s" : ""} · {eur(order.total)} · {order.pickup_time}
                        </p>
                        <p className="flex items-center gap-[1.5cqw] text-[2.8cqw] text-[#78716C] mt-[1.5cqw]">
                          <MapPin size={12} className="shrink-0" /> {RESTAURANT.address}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
